/**
 * Unified Settings API abstraction layer.
 *
 * Features (read-only):    /api/v1/features, /api/v1/features/:type
 * Laminar toggle:          /api/v1/features/laminar (PUT { enabled })
 * Metriq toggle:           /api/v1/features/metriq  (PUT { enabled })
 * Observe toggle:          /api/v1/features/observe (PUT { enabled })
 * Versions (all services): /api/v1/versions/*
 */
import apiClient from '@/lib/api/client';
import { AGENT_SYNC_URL } from '@/lib/api/release-manager';
import { FEATURE_FLAG_REGISTRY, PRODUCT_REGISTRY } from '../config';
import type { CurrentPlatformRelease, FeatureFlag, PlatformRelease, Product } from '../types';

// ─── API response types ───────────────────────────────────────────────

interface FeaturesResponse {
  items: Array<{
    feature: string;
    displayName: string;
    enabled: boolean;
    status: string;
    state?: string;
    name?: string;
  }>;
  total: number;
}

interface FeatureStatusResponse {
  feature: string;
  displayName: string;
  enabled: boolean;
  status: string;
  state?: string;
  name?: string;
}

interface VersionsListResponse {
  items: Array<{
    service: string;
    displayName: string;
    name: string;
    currentVersion: string;
    status: string;
    releaseRef?: string;
  }>;
  total: number;
}

interface AvailableVersionsResponse {
  service: string;
  versions: Array<{
    version: string;
    name: string;
    createdAt: string;
    deprecated: boolean;
  }>;
  total: number;
}

interface VersionStatusResponse {
  items: Array<{
    service: string;
    name: string;
    status: string;
    currentVersion: string;
    targetVersion?: string;
    message?: string;
  }>;
  total: number;
  hasActiveOperations: boolean;
}

interface PlatformReleasesResponse {
  items: Array<{
    name: string;
    version: string;
    autoDeploy: boolean;
    deprecated: boolean;
    deploymentStatus: string;
    deploymentMessage?: string;
    deployedAt?: string;
    componentStatuses?: Array<{ component: string; status: string; message?: string }>;
    releaseNotes?: string;
    createdAt: string;
    deployTriggered?: boolean;
  }>;
  total: number;
}

interface CurrentPlatformReleaseResponse {
  name?: string;
  version?: string;
  deploymentStatus?: string;
  deployedAt?: string;
  componentStatuses?: Array<{ component: string; status: string; message?: string }>;
}

// ─── Toggle API mapping ───────────────────────────────────────────────

/** Maps feature flag IDs to their CRUD endpoints for enable/disable */
const FEATURE_TOGGLE_ENDPOINTS: Record<string, string> = {
  laminar: '/features/laminar',
  metriq: '/features/metriq',
  observe: '/features/observe',
};

/** Service keys used in the versions API (for list, get, upgrade) */
const VERSION_SERVICE_KEYS: Record<string, string> = {
  'metadata-service': 'metadataservice',
  laminar: 'laminar',
  metriq: 'metriq',
  observe: 'observe',
};

/** Service keys used in the versions/status query param (may differ from VERSION_SERVICE_KEYS) */
const VERSION_STATUS_KEYS: Record<string, string> = {
  'metadata-service': 'metadataservices',
  laminar: 'laminar',
  metriq: 'metriq',
  observe: 'observe',
};

export const settingsApi = {
  // ─── Features ──────────────────────────────────────────────────────

  /**
   * List all feature flags with their current state.
   * GET /api/v1/features (read-only aggregation for laminar)
   */
  getFeatures: async (): Promise<FeatureFlag[]> => {
    let apiFeatures: FeaturesResponse['items'] = [];
    try {
      const res = await apiClient.get<FeaturesResponse>('/features');
      apiFeatures = res.items;
    } catch {
      // API not available yet — fall back to defaults
    }

    return FEATURE_FLAG_REGISTRY.map((entry) => {
      const apiFlag = apiFeatures.find((f) => f.feature === entry.id);
      if (apiFlag) {
        return {
          id: entry.id,
          title: apiFlag.displayName,
          description: entry.description,
          enabled: apiFlag.enabled,
          status: apiFlag.status,
          state: apiFlag.state,
          name: apiFlag.name,
        };
      }
      // API returned nothing for this flag — show as disabled
      return { ...entry, enabled: false };
    });
  },

  /**
   * Toggle a feature flag.
   * Laminar: PUT /api/v1/laminar { enabled }
   */
  updateFeature: async (flagId: string, enabled: boolean): Promise<void> => {
    const endpoint = FEATURE_TOGGLE_ENDPOINTS[flagId];
    if (!endpoint) {
      throw new Error(`Unknown feature flag: ${flagId}`);
    }
    await apiClient.put(endpoint, { enabled });
  },

  /**
   * Poll status after a feature flag toggle.
   * GET /api/v1/features/:type
   *
   * Returns `state` alongside `status` so callers can detect the terminal
   * 'not_provisioned' case (CR never created, e.g. update-only features) and
   * stop polling instead of waiting forever on an empty status.
   */
  getFeatureStatus: async (flagId: string): Promise<{ status: string; state?: string }> => {
    const endpoint = FEATURE_TOGGLE_ENDPOINTS[flagId];
    if (!endpoint) {
      return { status: 'Ready' };
    }
    try {
      const res = await apiClient.get<FeatureStatusResponse>(`/features/${flagId}`);
      return { status: res.status, state: res.state };
    } catch {
      return { status: 'Unknown' };
    }
  },

  // ─── Products (Versions) ───────────────────────────────────────────

  /**
   * List all products with current version and status.
   * ALL services: GET /api/v1/versions
   */
  getProducts: async (): Promise<Product[]> => {
    let apiServices: VersionsListResponse['items'] = [];
    try {
      const res = await apiClient.get<VersionsListResponse>('/versions');
      apiServices = res.items;
    } catch {
      // API not available
    }

    return PRODUCT_REGISTRY.map((entry) => {
      const serviceKey = VERSION_SERVICE_KEYS[entry.id];
      const svc = apiServices.find((s) => s.service === serviceKey);
      if (svc) {
        return {
          id: entry.id,
          title: entry.title,
          description: entry.description,
          currentVersion: svc.currentVersion || 'Not deployed',
          status: svc.status,
        };
      }
      return {
        id: entry.id,
        title: entry.title,
        description: entry.description,
        currentVersion: 'Not deployed',
        status: 'Unknown',
      };
    });
  },

  /**
   * Get available versions for a product.
   * ALL services: GET /api/v1/versions/:service
   */
  getProductVersions: async (productId: string): Promise<string[]> => {
    const serviceKey = VERSION_SERVICE_KEYS[productId];
    if (!serviceKey) throw new Error(`Unknown product: ${productId}`);

    const res = await apiClient.get<AvailableVersionsResponse>(`/versions/${serviceKey}`);
    return res.versions.filter((v) => !v.deprecated).map((v) => v.version);
  },

  /**
   * Ask the release agent to pull fresh versions immediately.
   * POST /api/v1/agent/sync — same call the cluster form's version refresh makes.
   */
  syncVersionAgent: async (): Promise<void> => {
    await apiClient.post(AGENT_SYNC_URL);
  },

  /**
   * Trigger a version upgrade for a product.
   * ALL services: POST /api/v1/versions/:service/upgrade
   */
  upgradeProduct: async (productId: string, version: string): Promise<void> => {
    const serviceKey = VERSION_SERVICE_KEYS[productId];
    if (!serviceKey) throw new Error(`Unknown product: ${productId}`);

    await apiClient.post(`/versions/${serviceKey}/upgrade`, { version });
  },

  /**
   * Poll current status of a product (used during upgrades).
   * ALL services: GET /api/v1/versions/status?services=...
   *
   * Returns `hasActiveOperations` so the polling hook can detect the case
   * where the operator has removed the upgrade from the active list (status
   * settled faster than the poll interval and never showed up in items).
   * Without this signal we used to spin forever returning 'Unknown'.
   */
  getProductStatus: async (
    productId: string
  ): Promise<{ status: string; hasActiveOperations: boolean }> => {
    const statusKey = VERSION_STATUS_KEYS[productId];
    if (!statusKey) throw new Error(`Unknown product: ${productId}`);

    try {
      const res = await apiClient.get<VersionStatusResponse>('/versions/status', {
        params: { services: statusKey },
      });
      const item = res.items[0];
      return {
        status: item?.status ?? 'Unknown',
        hasActiveOperations: res.hasActiveOperations,
      };
    } catch {
      return { status: 'Unknown', hasActiveOperations: false };
    }
  },

  // ─── Platform Releases ────────────────────────────────────────────

  getPlatformReleases: async (): Promise<PlatformRelease[]> => {
    try {
      const res = await apiClient.get<PlatformReleasesResponse>('/platform/releases');
      return res.items ?? [];
    } catch {
      return [];
    }
  },

  getCurrentPlatformRelease: async (): Promise<CurrentPlatformRelease> => {
    try {
      return await apiClient.get<CurrentPlatformReleaseResponse>('/platform/releases/current');
    } catch {
      return {};
    }
  },

  deployPlatformRelease: async (name: string): Promise<void> => {
    await apiClient.post(`/platform/releases/${name}/deploy`, {});
  },
};
